import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ChevronRight, Clock, Calendar } from 'lucide-react';
import { postsAPI, categoriesAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import CTASection from '../components/shared/CTASection';
import LeadForm from '../components/shared/LeadForm';
import { usePageMeta } from '../components/shared/SEO';

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [postsRes, featuredRes, categoriesRes] = await Promise.all([
        postsAPI.getAll({
          status: 'published',
          search: searchParams.get('search') || undefined,
          category: searchParams.get('category') || undefined,
          page: parseInt(searchParams.get('page') || '1'),
        }),
        postsAPI.getFeatured(3),
        categoriesAPI.getAll(),
      ]);

      setPosts(postsRes.data.posts);
      setPagination({
        page: postsRes.data.page,
        pages: postsRes.data.pages,
        total: postsRes.data.total,
      });
      setFeaturedPosts(featuredRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleCategoryFilter = (categoryId) => {
    const params = new URLSearchParams(searchParams);
    if (categoryId) {
      params.set('category', categoryId);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSelectedCategory(categoryId);
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  usePageMeta({
    title: 'Blog - Apex Medical Hub',
    description: 'Conteúdos estratégicos para médicos, clínicas e consultórios. Artigos sobre contabilidade médica, tributação e gestão.',
    canonical: '/blog',
  });

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-white pt-28 pb-10" data-testid="blog-hero">
        <div className="container-apex">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Blog Apex Medical Hub
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              Conteúdos estratégicos para médicos, clínicas e consultórios que buscam mais organização, eficiência, segurança e crescimento sustentável.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Pesquisar artigos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-base rounded-full border-slate-200 focus:border-primary"
                  data-testid="blog-search-input"
                />
                <Button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary rounded-full px-6"
                  data-testid="blog-search-button"
                >
                  Buscar
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="border-b border-slate-100 bg-white sticky top-20 z-30" data-testid="blog-categories">
        <div className="container-apex">
          <div className="flex items-center gap-2 py-4 overflow-x-auto scrollbar-hide">
            <span className="text-sm font-medium text-slate-500 mr-2">Categorias:</span>
            <button
              onClick={() => handleCategoryFilter('')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !selectedCategory
                  ? 'bg-[#1e4e7e] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="category-filter-all"
            >
              Todos
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryFilter(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-[#1e4e7e] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`category-filter-${category.slug}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {!searchParams.get('search') && !searchParams.get('category') && featuredPosts.length > 0 && (
        <section className="section-padding bg-slate-50" data-testid="blog-featured">
          <div className="container-apex">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-8">
              Em destaque
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Main Featured */}
              {featuredPosts[0] && (
                <Link
                  to={`/blog/${featuredPosts[0].slug}`}
                  className="group relative rounded-2xl overflow-hidden aspect-[16/10] lg:aspect-auto lg:row-span-2"
                  data-testid="featured-post-main"
                >
                  <img
                    src={featuredPosts[0].featured_image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800'}
                    alt={featuredPosts[0].title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ objectPosition: featuredPosts[0].featured_image_position || 'center' }}
                    loading="lazy"
                  />
                  <div className="featured-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#5cc1b0] text-slate-900 mb-3">
                      {featuredPosts[0].category_name}
                    </span>
                    <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 group-hover:text-[#5cc1b0] transition-colors">
                      {featuredPosts[0].title}
                    </h3>
                    <p className="text-white/80 line-clamp-2 mb-4 hidden md:block">
                      {featuredPosts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-white/70">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(featuredPosts[0].published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {featuredPosts[0].reading_time} min
                      </span>
                    </div>
                  </div>
                </Link>
              )}

              {/* Secondary Featured */}
              <div className="space-y-6">
                {featuredPosts.slice(1, 3).map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group flex gap-4 bg-white rounded-xl p-4 border border-slate-100 hover:shadow-md transition-all"
                    data-testid={`featured-post-${post.slug}`}
                  >
                    <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={post.featured_image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400'}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: post.featured_image_position || 'center' }}
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="category-badge mb-2">
                        {post.category_names?.length > 0 ? post.category_names[0] : post.category_name}
                      </span>
                      <h4 className="font-heading font-semibold text-slate-900 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h4>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span>{formatDate(post.published_at)}</span>
                        <span>{post.reading_time} min</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="section-padding" data-testid="blog-posts">
        <div className="container-apex">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Posts Grid */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-heading text-2xl font-bold text-slate-900">
                  {searchParams.get('search')
                    ? `Resultados para "${searchParams.get('search')}"`
                    : selectedCategory
                    ? 'Artigos da categoria'
                    : 'Artigos recentes'}
                </h2>
                <p className="text-slate-500 text-sm">
                  {pagination.total} artigo{pagination.total !== 1 ? 's' : ''}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {Array(4).fill(0).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden border border-slate-100">
                      <div className="skeleton h-48 w-full" />
                      <div className="p-6">
                        <div className="skeleton h-4 w-24 mb-3" />
                        <div className="skeleton h-6 w-full mb-2" />
                        <div className="skeleton h-4 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : posts.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {posts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog/${post.slug}`}
                        className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all duration-300"
                        data-testid={`blog-post-${post.slug}`}
                      >
                        <div className="blog-card-image">
                          <img
                            src={post.featured_image || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800'}
                            alt={post.title}
                            style={{ objectPosition: post.featured_image_position || 'center' }}
                            loading="lazy"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {post.category_names && post.category_names.length > 0 ? (
                              post.category_names.slice(0, 2).map((name, i) => (
                                <span key={i} className="category-badge">{name}</span>
                              ))
                            ) : (
                              <span className="category-badge">{post.category_name}</span>
                            )}
                          </div>
                          <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-sm text-slate-500">
                            <span>{formatDate(post.published_at)}</span>
                            <span>{post.reading_time} min de leitura</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-12">
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="btn-secondary"
                      >
                        Anterior
                      </Button>
                      <span className="px-4 text-sm text-slate-600">
                        Página {pagination.page} de {pagination.pages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        className="btn-secondary"
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <p className="text-slate-500 mb-4">Nenhum artigo encontrado.</p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('');
                      setSearchParams({});
                    }}
                    className="btn-secondary"
                  >
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-8">
              {/* Newsletter */}
              <LeadForm
                title="Receba conteúdos exclusivos"
                description="Cadastre-se para receber artigos e dicas no seu e-mail."
                source="blog-sidebar"
                showClinicField={false}
              />

              {/* Categories List */}
              <div className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                  Categorias
                </h3>
                <ul className="space-y-2">
                  {categories.map((category) => (
                    <li key={category.id}>
                      <button
                        onClick={() => handleCategoryFilter(category.id)}
                        className="flex items-center justify-between w-full py-2 px-3 rounded-lg text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-slate-700">{category.name}</span>
                        <span className="text-sm text-slate-400">{category.article_count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection variant="light" />
    </div>
  );
};

export default BlogPage;
